/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import Text from 'src/components/ts-components/Text'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { buildTeamSearchText } from '@sportninja/common/features/registration/teamHistory'
import TeamOptionLabel from '../components/TeamHistory/TeamOptionLabel'
import TeamSelectedLabel from '../components/TeamHistory/TeamSelectedLabel'
import type { TeamHistoryTools } from '../hooks/useTeamHistory'

export const CREATE_NEW_TEAM_VALUE = 'create_new_team'

const createNewTeamLabel = (
  <Text
    variant='paragraphMedium'
    color={isCanlan ? colors.NEUTRAL_0 : colors.PRIMARY}
    weight='medium'
  >
    Create New Team
  </Text>
)

/**
 * 🔄 getTeamDropdownOptions: Generates dropdown options for team selection
 * Handles both teams and drafts (buckets) with proper formatting
 *
 * Every option also carries:
 * - `searchText` — what the select's search matches (see `matchesTeamSearch`)
 * - `selectedLabel` — what the closed select shows (`optionLabelProp`)
 *
 * With `teamHistory`, any team whose payload carries history renders it
 * (competitions, players, last competition, officials) and collapses to logo +
 * name once chosen. A team without history keeps the plain row.
 */
export const getTeamDropdownOptions = (
  userTeams: any[],
  isTeamRep: boolean,
  showExistingRegistrationsPlayers: boolean = true,
  teamHistory?: TeamHistoryTools
): any[] => {
  console.log('🔄 getTeamDropdownOptions: Generating team dropdown options', {
    userTeamsCount: userTeams?.length || 0,
    isTeamRep,
    showExistingRegistrationsPlayers,
  })

  const buckets = userTeams?.filter((team) => team.is_bucket) || []
  const teams = userTeams?.filter((team) => !team.is_bucket) || []
  const options: any[] = []

  // Only show buckets section if not team rep and buckets exist
  if (!isTeamRep && buckets.length > 0) {
    options.push({
      label: (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='semiBold'
            color={colors.PRIMARY}
            style='text-transform: uppercase;'
          >
            Drafts
          </Text>
          <Tooltip title="A Draft Is A Temporary Team Used To Group Players Who Don't Have A Team Yet. Players In Drafts Will Be Drafted Or Assigned To Teams Later.">
            <InfoCircleOutlined
              css={css`
                color: ${colors.PRIMARY};
                font-size: 16px;
                margin-left: 8px;
              `}
            />
          </Tooltip>
        </div>
      ),
      title: 'drafts',
      options: buckets.map((el) => {
        const label = (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: 12px;
            `}
          >
            <img src={BUCKET_IMAGE_SRC} width={24} height={24} />
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                color={colors.WHITE}
                weight='regular'
              >
                {el.name}
              </Text>
            </div>
          </div>
        )
        return {
          label,
          selectedLabel: label,
          searchText: buildTeamSearchText(el),
          value: el?.team_player_roster_id || el.uid || el.id,
        }
      }),
    })
  }

  if (isTeamRep && teams.length === 0) {
    return [
      {
        label: createNewTeamLabel,
        selectedLabel: createNewTeamLabel,
        value: CREATE_NEW_TEAM_VALUE,
      },
    ]
  }

  // Only show teams section if there are teams or is team rep
  if (teams.length > 0 || isTeamRep) {
    options.push({
      label: (
        <>
          {teams?.length > 0 ? (
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='semiBold'
                color={colors.PRIMARY}
                style='text-transform: uppercase;'
              >
                Teams
              </Text>
            </div>
          ) : (
            <div
              css={css`
                display: none;
                .ant-select-item ant-select-item-group {
                  display: none;
                }
              `}
            />
          )}
        </>
      ),
      title: 'teams',
      options: [
        ...(teams.length > 0
          ? teams.map((el) => {
              const history = teamHistory ? teamHistory.buildHistory(el) : null
              const label =
                history && teamHistory ? (
                  <TeamOptionLabel
                    team={el}
                    history={history}
                    copy={teamHistory.copy}
                  />
                ) : (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      align-items: center;
                      gap: 12px;
                    `}
                  >
                    <img
                      src={
                        getImageThumbnailId(el) ||
                        '/images/generic_placeholders/placeholder_team.png'
                      }
                      width={24}
                      height={24}
                      onError={(e: any) => {
                        e.target.onerror = null
                        e.target.src =
                          '/images/generic_placeholders/placeholder_team.png'
                      }}
                    />
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                      `}
                    >
                      <Text
                        variant='paragraphSmall'
                        color={colors.WHITE}
                        weight='regular'
                      >
                        {el.name}
                      </Text>
                      {!isTeamRep && showExistingRegistrationsPlayers ? (
                        <Text
                          variant='captionXSmall'
                          color={
                            el.max_players_per_team &&
                            el.player_count >= el.max_players_per_team
                              ? colors.ERROR_200
                              : colors.NEUTRAL_50
                          }
                          weight='regular'
                        >
                          {el.player_count}
                          {el.max_players_per_team
                            ? `/${el.max_players_per_team}`
                            : ''}{' '}
                          {`player${el.player_count > 1 ? 's' : ''}`}
                          {el.max_players_per_team &&
                          el.player_count >= el.max_players_per_team
                            ? ' - Team is full'
                            : ''}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                )
              return {
                label,
                selectedLabel: history ? (
                  <TeamSelectedLabel team={el} />
                ) : (
                  label
                ),
                searchText: buildTeamSearchText(el),
                value: el?.team_player_roster_id || el.uid || el.id,
                disabled:
                  el.max_players_per_team &&
                  el.player_count >= el.max_players_per_team,
              }
            })
          : []),
        ...(isTeamRep
          ? [
              {
                label: createNewTeamLabel,
                selectedLabel: createNewTeamLabel,
                value: CREATE_NEW_TEAM_VALUE,
              },
            ]
          : []),
      ],
    })
  }

  return options
}
